import { useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import PageMeta from "../components/common/PageMeta";
import AuthLayout from "./AuthPages/AuthPageLayout";
import Alert from "../components/ui/alert/Alert";
import Button from "../components/ui/button/Button";
import { useStateValue } from "../context/StateValueContext";
import { useApi } from "../hooks/useApi";
import { auth } from "../../config/firebase.js";

export default function ShopifyInstallError() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [{ user }] = useStateValue();
  const { post } = useApi();

  const reason = searchParams.get('reason') || '';

  // If we landed here because cm_details was missing but the user IS authenticated
  // in this tab, proactively call post-login to re-set the cookie. By the time the
  // merchant returns to Shopify admin and re-initiates install, the cookie will be
  // in place and the next attempt will succeed.
  useEffect(() => {
    if (reason === 'missing_cookie' && user) {
      post('/api/users/post-login', {}, { includeCredentials: true }).catch((err) => {
        console.error('Post-login API call failed:', err);
      });
    }
  }, [reason, user, post]);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
    } catch (err) {
      console.error('Sign-out failed:', err);
    }
    navigate('/signin');
  };

  let content;
  if (reason === 'missing_cookie') {
    if (user) {
      content = (
        <Alert
          variant="warning"
          title="Your Shopify install could not be completed"
          message="Please re-initiate the install from your Shopify admin."
        />
      );
    } else {
      content = (
        <>
          <Alert
            variant="info"
            title="Sign in to complete your Shopify install"
            message="Please sign in to your Chargemont account, then re-initiate the install from your Shopify admin."
          />
          <Link to="/signin" className="block mt-5">
            <Button className="w-full" size="sm" variant="primary">
              Sign in
            </Button>
          </Link>
        </>
      );
    }
  } else if (reason === 'wrong_role') {
    if (user) {
      content = (
        <>
          <Alert
            variant="warning"
            title="Wrong account type"
            message={`You are signed in as ${user.email} (${user.role}). To install the Shopify app, please sign in with a client admin account.`}
          />
          <Button
            className="w-full mt-5"
            size="sm"
            variant="primary"
            onClick={handleSignOut}
          >
            Sign out
          </Button>
        </>
      );
    } else {
      // Edge case: backend reported wrong_role but the client-side auth state
      // has since cleared. Fall back to the "please sign in" flow.
      content = (
        <>
          <Alert
            variant="info"
            title="Sign in to complete your Shopify install"
            message="Please sign in with a client admin account, then re-initiate the install from your Shopify admin."
          />
          <Link to="/signin" className="block mt-5">
            <Button className="w-full" size="sm" variant="primary">
              Sign in
            </Button>
          </Link>
        </>
      );
    }
  } else if (reason === 'user_not_found') {
    content = (
      <Alert
        variant="error"
        title="Account not found"
        message="Your account could not be found. Please contact support."
      />
    );
  } else {
    content = (
      <Alert
        variant="error"
        title="Shopify install failed"
        message="We could not complete the install. Please re-initiate the install from your Shopify admin."
      />
    );
  }

  return (
    <>
      <PageMeta
        title="Chargemont | Shopify install error"
        description="Chargemont | Shopify install error"
      />
      <AuthLayout>
        <div className="flex flex-col flex-1">
          <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
            <div className="mb-5 sm:mb-8">
              <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                Shopify install
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                We ran into an issue completing your Shopify app install.
              </p>
            </div>
            {content}
          </div>
        </div>
      </AuthLayout>
    </>
  );
}
